import type { InformationContainerSize } from '@components/VirtualBackgrounds/types';
import { IntegrationServices } from '@constants/integration';
import { customAlphabet } from 'nanoid';
import type { CardUrl } from 'types';

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * A function that emits a side effect and does not return anything.
 */
export type Procedure = (...args: any[]) => void;

export interface DebouncedFunction<F extends Procedure> {
  (this: ThisParameterType<F>, ...args: Parameters<F>): void;
  cancel: () => void;
}

export interface ThrottledFunction<F extends Procedure> {
  (this: ThisParameterType<F>, ...args: Parameters<F>): void;
}

export type DebounceOptions = {
  isImmediate: boolean;
};

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @param func Function to invoke after the debouncing ends
 * @param waitMilliseconds Time until function is invoked if it isn't debounced
 * @param options Allow for triggering the function provided immediately
 */
export function debounce<F extends Procedure>(
  func: F,
  waitMilliseconds = 50,
  options: DebounceOptions = {
    isImmediate: false,
  },
): DebouncedFunction<F> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const debouncedFunction = function (this: ThisParameterType<F>, ...args: Parameters<F>) {
    const doLater = () => {
      timeoutId = undefined;
      if (!options.isImmediate) {
        func.apply(this, args);
      }
    };

    const shouldCallNow = options.isImmediate && timeoutId === undefined;

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(doLater, waitMilliseconds);

    if (shouldCallNow) {
      func.apply(this, args);
    }
  };

  debouncedFunction.cancel = function () {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };

  return debouncedFunction;
}

/**
 * Returns a function that can only be invoked once within a time interval whose duration you supply.
 * @param func Function to invoke once during an interval
 * @param interval The duration for which the function can only be called once
 */
export function throttle<F extends Procedure>(func: F, interval: number): ThrottledFunction<F> {
  let tick = false;

  return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
    if (!tick) {
      func.apply(this, args);
      tick = true;
      setTimeout(() => {
        tick = false;
      }, interval);
    }
  };
}

/**
 * Throttles and debounces your function. You can think of this as throttling + one extra invocation
 * after the returned function hasn't been invoked for an amount of time (the interval)
 * @param callback The function whose invocation should be controlled
 * @param interval The duration for which the function is debounced and throttled
 */
export function throttleDebounce<F extends Procedure>(
  callback: F,
  interval: number,
): (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
  const throttleCallback = throttle(callback, interval);
  const debounceCallback = debounce(callback, interval);
  return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
    throttleCallback.apply(this, args);
    debounceCallback.apply(this, args);
  };
}

/**
 * Checks whether a given point is with a triangle made up of the three vertices supplied
 * @param px The x-coordinate of the point to check
 * @param py The y-coordinate of the point to check
 * @param ax Vertex 1 x-coordinate
 * @param ay Vertex 1 y-coordinate
 * @param bx Vertex 2 x-coordinate
 * @param by Vertex 2 y-coordinate
 * @param cx Vertex 3 x-coordinate
 * @param cy Vertex 3 y-coordinate
 */
export function isInTriangle(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): boolean {
  //credit: http://www.blackpawn.com/texts/pointinpoly/default.html

  const v0 = [cx - ax, cy - ay];
  const v1 = [bx - ax, by - ay];
  const v2 = [px - ax, py - ay];

  const dot00 = v0[0] * v0[0] + v0[1] * v0[1];
  const dot01 = v0[0] * v1[0] + v0[1] * v1[1];
  const dot02 = v0[0] * v2[0] + v0[1] * v2[1];
  const dot11 = v1[0] * v1[0] + v1[1] * v1[1];
  const dot12 = v1[0] * v2[0] + v1[1] * v2[1];

  const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);

  const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
  const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

  return u >= 0 && v >= 0 && u + v < 1;
}

/**
 * Generates a hash using a supplied string and salt. This is mainly meant for use in components where
 * the str would be a value (doesn't need to be unique across all components) and the salt would be an
 * ID unique to the component (use @reach/useId for this)
 * @param {string} str A string value of any length
 * @param {string} salt Another string that should be unique to a given component
 */
export function makeHash(str: string, salt: string): string {
  let hash = 0;
  const saltedStr = salt ? `${str}__${salt}` : str;
  if (str.length === 0) {
    return hash.toString();
  }
  for (let i = 0; i < saltedStr.length; i++) {
    const char = saltedStr.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString();
}

/**
 * Wraps a lib-defined event handler and a user-defined event handler, returning
 * a single handler that allows a user to prevent lib-defined handlers from
 * firing.
 *
 * @param theirHandler User-supplied event handler
 * @param ourHandler Library-supplied event handler
 */

export function wrapEvent<EventType extends React.SyntheticEvent | Event>(
  theirHandler: ((event: EventType) => void) | undefined,
  ourHandler: (event: EventType) => void,
): (event: EventType) => void {
  return (event) => {
    theirHandler && theirHandler(event);
    if (!event.defaultPrevented) {
      return ourHandler(event);
    }
  };
}

export function createOrAddToArray<T>(array: Array<T> | undefined, value: T): Array<T> {
  if (Array.isArray(array)) {
    return [...array, value];
  } else {
    return [value];
  }
}

export function modifyArrayAtIndex<T>(
  array: Array<T> | undefined,
  index: number,
  value: T,
): Array<T> {
  if (!array) return [value];

  return array.reduce(
    (array, arrayValue, i) => {
      if (i === index) return [...array, value];
      else return [...array, arrayValue];
    },
    [] as Array<T>,
  );
}

export function removeArrayValueAtIndex<T>(
  array: Array<T> | undefined,
  index: number,
): Array<T> | undefined {
  if (!array) return undefined;

  return array.filter((arrayValue, i) => i !== index);
}

export function toCamelCase(str: string): string {
  return str.replace(/([-_][a-z])/gi, (matchingGroup) => {
    return matchingGroup.toUpperCase().replace('-', '').replace('_', '');
  });
}

export function toServiceName(service?: string): string {
  switch (service) {
    case IntegrationServices.HUBSPOT:
      return 'HubSpot';
    case IntegrationServices.MARKETO:
      return 'Marketo';
    case IntegrationServices.ZAPIER:
      return 'Zapier';
    case IntegrationServices.SALESFORCE:
      return 'Salesforce';
    case IntegrationServices.MS_DYNAMICS_SALES:
      return 'Dynamics 365 Sales';
    case IntegrationServices.OUTLOOK:
      return 'Outlook';
    case IntegrationServices.PIPEDRIVE:
      return 'Pipedrive';
    case IntegrationServices.ZOHO_CRM:
      return 'Zoho';
    case IntegrationServices.CHILI_PIPER:
      return 'Chili Piper';
    case IntegrationServices.GOHIGHLEVEL:
      return 'GoHighLevel';
    case IntegrationServices.MONDAY:
      return 'Monday';
    default:
      return '';
  }
}

export function ensure<T>(argument: T | undefined | null, message = 'Missing data'): T {
  if (argument === undefined || argument === null) {
    throw new TypeError(message);
  }

  return argument;
}

export function formatUrl(type: CardUrl['type'] | string, url: string): string {
  if (!/^https?:\/\//i.test(url)) {
    switch (type) {
      case 'skype':
        // Skype uses its own URI format (skype:username?call) instead of http for linking.
        // The URL value retrieved from the DB is in the correct format, so we can just return the URL here.
        return url;
      case 'org':
      case 'calendar':
      case 'link':
        // All secure sites will upgrade to https anyway so this at least supports sites that use http.
        return `http://${url}`;
      default:
        // We use https for any non generic link though to avoid a useless redirect.
        return `https://${url}`;
    }
  }
  return url;
}

export function generateEntityId(idLength = 12): string {
  return customAlphabet(alphabet, idLength)();
}

export function modifyArrayAtId<T extends { id: string }>(
  array: Array<T> | undefined,
  id: string,
  value: T,
): Array<T> {
  if (!array) return [value];

  return array.reduce(
    (array, arrayValue) => {
      if (arrayValue.id === id) return [...array, value];
      else return [...array, arrayValue];
    },
    [] as Array<T>,
  );
}

export function removeArrayValueAtId<T extends { id: string }>(
  array: Array<T> | undefined,
  id: string,
): Array<T> | undefined {
  if (!array) return undefined;

  return array.filter((arrayValue) => arrayValue.id !== id);
}

export function toggleArrayValueAtId(
  array: string[],
  value: string,
  toggleState?: boolean,
): string[] {
  if (toggleState !== true && array.includes(value)) {
    return array.filter((arrayValue) => arrayValue !== value);
  } else if (toggleState !== false) return [...new Set([...array, value])];
  else return array;
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

/**
 * @deprecated Use copyToClipboard instead
 * this uses deprecated execCommand which is not supported in all browsers
 */
export function copyText(
  targetNode: HTMLElement | ChildNode | undefined,
  keepSelected = false,
): boolean {
  if (!targetNode) return false;

  if (targetNode.nodeName === 'INPUT' || targetNode.nodeName === 'TEXTAREA') {
    (targetNode as HTMLInputElement | HTMLTextAreaElement).select();

    try {
      // Now that we've selected the anchor text, execute the copy command
      const successful = document.execCommand('copy');
      getSelection()?.removeAllRanges();
      return successful;
    } catch {
      return false;
    }
  } else {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(targetNode);
    selection?.removeAllRanges();
    selection?.addRange(range);

    try {
      // Now that we've selected the anchor text, execute the copy command
      const successful = document.execCommand('copy');
      if (!keepSelected) selection?.removeAllRanges();
      return successful;
    } catch {
      return false;
    }
  }
}

export function areArraysEqual<T>(arr1: T[] | undefined, arr2: T[] | undefined): boolean {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;

  const matchedIndexes = new Set();

  return arr1.every((value1) => {
    return arr2.some((value2, index2) => {
      if (value1 === value2 && !matchedIndexes.has(index2)) {
        matchedIndexes.add(index2);
        return true;
      } else return false;
    });
  });
}

/**
 * Detects right clicks
 *
 * @param nativeEvent
 */
export function isRightClick(nativeEvent: MouseEvent | PointerEvent | TouchEvent): boolean {
  if ('button' in nativeEvent) {
    // IE, Opera
    return nativeEvent.button == 2;
  } else if ('which' in nativeEvent) {
    // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
    return nativeEvent.which == 3;
  } else return false;
}

export function noop(): void {
  // Empty
}

export function isNonEmptyArray(array: unknown): boolean {
  return !!array && array instanceof Array && array.length > 0;
}

/**
 * Moves the item to the new position in the array array. Useful for huge arrays where absolute performance is needed.
 * @param array Array to be reordered
 * @param from Index of item to move. If negative, it will begin that many elements from the end.
 * @param to Index of where to move the item. If negative, it will begin that many elements from the end.
 */
export function arrayMoveMutate<T>(array: Array<T>, from: number, to: number): void {
  const startIndex = from < 0 ? array.length + from : from;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = to < 0 ? array.length + to : to;

    const [item] = array.splice(from, 1);
    array.splice(endIndex, 0, item);
  }
}

/**
 * Clones the given array, moves the item to a new position in the new array, and then returns the new array. The given array is not mutated.
 * @param array Array to be reordered
 * @param from Index of item to move. If negative, it will begin that many elements from the end.
 * @param to Index of where to move the item. If negative, it will begin that many elements from the end.
 */
export function arrayMove<T>(array: Array<T>, from: number, to: number): Array<T> {
  array = [...array];
  arrayMoveMutate(array, from, to);
  return array;
}

/**
 * Linear interpolation between numbers.
 */
export function lerp(x: number, y: number, a: number): number {
  return x * (1 - a) + y * a;
}

/**
 * Transforms a size string enum into a corresponding number.
 */
export function transformDetailSectionSizeToScalar(size: InformationContainerSize): number {
  switch (size) {
    case 'small':
      return 0.8;
    case 'medium':
      return 1;
    case 'large':
      return 1.2;
  }
}

/**
 * A shorthand to check whether a string value can be parsed as a HTTP or HTTPS URL.
 */
export function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

export function removeUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(obj)
    .filter(([, val]) => val !== undefined)
    .reduce(
      (acc, [key, val]) => ({
        ...acc,
        [key]: val === Object(val) ? removeUndefined(val as Record<string, unknown>) : val,
      }),
      {},
    );
}

/**
 * Type guard for not null or undefined
 *
 * To be used in a filter function
 * @example
 * const list = [1, 2, null, 3, undefined, 4];
 * const filteredList = list.filter(notNullOrUndefined);
 * // filteredList = [1, 2, 3, 4]
 *
 * @param value the value which could be null or undefined
 * @returns a type guard for not null or undefined
 */
export function notNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}
